import { useEffect, useMemo, useState } from 'react';
import { getContestTime } from '../../../../auth/_requests';
import type { DrawCountdown } from '../rewardsPageTypes';

type ContestTimeState = {
  loading: boolean;
  countdown: DrawCountdown;
  startTime?: string;
  endTime?: string;
  error?: string;
};

const empty: DrawCountdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };

const clampCountdown = (c: DrawCountdown): DrawCountdown => ({
  days: Math.max(0, Number(c.days) || 0),
  hours: Math.max(0, Number(c.hours) || 0),
  minutes: Math.max(0, Number(c.minutes) || 0),
  seconds: Math.max(0, Number(c.seconds) || 0),
});

const tickDown = (c: DrawCountdown): DrawCountdown => {
  const total =
    c.days * 86400 +
    c.hours * 3600 +
    c.minutes * 60 +
    c.seconds;
  const next = Math.max(0, total - 1);
  return {
    days: Math.floor(next / 86400),
    hours: Math.floor((next % 86400) / 3600),
    minutes: Math.floor((next % 3600) / 60),
    seconds: next % 60,
  };
};

export const useContestTime = (opts: { token?: string; enabled: boolean; mode?: 'start' | 'end' }) => {
  const { token, enabled, mode = 'end' } = opts;
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState<DrawCountdown>(empty);
  const [startTime, setStartTime] = useState<string | undefined>(undefined);
  const [endTime, setEndTime] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!enabled || !token) return;

    let interval: number | undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const res = await getContestTime(token);
        if (cancelled) return;
        setStartTime(res.data.startTime);
        setEndTime(res.data.endTime);
        const base =
          mode === 'start' ? res.data.remainingStartTimeFromNow : res.data.remainingEndTimeFromNow;
        setCountdown(clampCountdown(base));
        interval = window.setInterval(() => {
          setCountdown((prev) => tickDown(prev));
        }, 1000);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Unknown error');
        setCountdown(empty);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (interval) window.clearInterval(interval);
    };
  }, [enabled, token, mode]);

  return useMemo<ContestTimeState>(
    () => ({ loading, countdown, startTime, endTime, error }),
    [loading, countdown, startTime, endTime, error]
  );
};

